import React from "react";
import { parseReport } from "../../../../utils/reportParser";
import ReportDTO, { GenericReport } from "../../../../models/finance/Reports";
import { FinanceType, FinanceTypeDef } from "../../../../models/finance/FinanceType";
import { Box, Typography, useTheme, Grid } from "@mui/material";
import MonthlyReportsCategoryTableComponent, {
  MonthlyReportsCategoryTableRow,
} from "./MonthlyReportsCategoryTableComponent";
import MonthlyReportsPerDayChartComponent from "./MonthlyReportsPerDayChartComponent";
import MonthlyReportsActualVsPlannedComponent from "./MonthlyReportActualVsPlannedComponent";
import { ModernCard } from "../../../common/StyledComponents";
import { BudgetStorage } from "../../../../utils/BudgetStorage";
import { useCurrencyFormatter } from "../../../../contexts/CurrencyContext";

interface MonthlyReportsComponentPropos {
  report: ReportDTO;
  type: FinanceTypeDef;
  year: number;
  month: string;
}

const MonthlyReportsComponent: React.FC<MonthlyReportsComponentPropos> = ({
  report,
  type,
  year,
  month,
}) => {
  const theme = useTheme();
  const { formatCurrencyNumber } = useCurrencyFormatter();
  const genericReport: GenericReport = parseReport(
    report,
    type
  );

  const isExpense = type === FinanceType.EXPENSE;
  
  // Fetch budget data from storage
  const monthlyReportsPerCategory: MonthlyReportsCategoryTableRow[] =
    Object.entries(genericReport.valuePerCategory).map(
      ([categoryName, actual]) => {
        const planned = BudgetStorage.getCategoryBudget(year, month, categoryName, isExpense);
        return {
          categoryName: categoryName,
          actual: actual,
          planned: planned,
          difference: actual - planned,
        };
      }
    );

  const themeColor = isExpense ? theme.palette.error : theme.palette.success;
  
  // Different titles and messaging for expenses vs income
  const overviewTitle = isExpense ? "Budget Overview" : "Target Overview";
  const overviewSubtitle = isExpense 
    ? `${month} ${year} spending vs budget`
    : `${month} ${year} income vs targets`;

  return (
    <Box sx={{ width: "100%" }}>
      {/* Budget/Target Overview and Quick Stats - Improved spacing and proportions */}
      <Grid container spacing={4} sx={{ mb: 6 }}>
        {/* Budget/Target Overview - Better proportions */}
        <Grid item xs={12} lg={6}>
          <ModernCard sx={{ height: '100%', boxShadow: theme.shadows[2] }}>
            <Box sx={{ 
              p: 3, 
              borderBottom: `1px solid ${theme.palette.divider}`,
              background: `linear-gradient(135deg, ${themeColor.light}10 0%, ${themeColor.main}05 100%)`,
            }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: themeColor.main, fontSize: '1.2rem', mb: 0.5 }}>
                {overviewTitle}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.9rem' }}>
                {overviewSubtitle}
              </Typography>
            </Box>
            <Box sx={{ 
              p: 4, 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              minHeight: 280,
            }}>
              <MonthlyReportsActualVsPlannedComponent
                totalAmount={genericReport.totalAmount}
                year={year}
                month={month}
                isExpense={type === FinanceType.EXPENSE}
                isCompact={true}
              />
            </Box>
          </ModernCard>
        </Grid>

        {/* Quick Stats - Better spacing and layout */}
        <Grid item xs={12} lg={6}>
          <ModernCard sx={{ height: '100%', boxShadow: theme.shadows[2] }}>
            <Box sx={{ 
              p: 3, 
              borderBottom: `1px solid ${theme.palette.divider}`,
              background: `linear-gradient(135deg, ${themeColor.light}10 0%, ${themeColor.main}05 100%)`,
            }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: themeColor.main, fontSize: '1.2rem', mb: 0.5 }}>
                Quick Stats
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.9rem' }}>
                Key metrics at a glance
              </Typography>
            </Box>
            <Box sx={{ p: 3 }}>
              <Grid container spacing={3}>
                <Grid item xs={6} sm={6}>
                  <Box sx={{ 
                    textAlign: 'center', 
                    p: 2.5, 
                    backgroundColor: theme.palette.grey[50], 
                    borderRadius: 2,
                    border: `1px solid ${theme.palette.grey[200]}`,
                    minHeight: 80,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                  }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.8rem', mb: 1, display: 'block' }}>
                      Categories
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 700, fontSize: '1.8rem', color: themeColor.main }}>
                      {Object.keys(genericReport.valuePerCategory).length}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={6}>
                  <Box sx={{ 
                    textAlign: 'center', 
                    p: 2.5, 
                    backgroundColor: theme.palette.grey[50], 
                    borderRadius: 2,
                    border: `1px solid ${theme.palette.grey[200]}`,
                    minHeight: 80,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                  }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.8rem', mb: 1, display: 'block' }}>
                      Active Days
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 700, fontSize: '1.8rem', color: themeColor.main }}>
                      {Object.values(genericReport.valuePerDay).filter(amount => amount > 0).length}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={6}>
                  <Box sx={{ 
                    textAlign: 'center', 
                    p: 2.5, 
                    backgroundColor: theme.palette.grey[50], 
                    borderRadius: 2,
                    border: `1px solid ${theme.palette.grey[200]}`,
                    minHeight: 80,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                  }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.8rem', mb: 1, display: 'block' }}>
                      Peak Day
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.1rem', color: themeColor.main }}>
                      {formatCurrencyNumber(Math.max(...Object.values(genericReport.valuePerDay), 0))}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={6}>
                  <Box sx={{ 
                    textAlign: 'center', 
                    p: 2.5, 
                    backgroundColor: theme.palette.grey[50], 
                    borderRadius: 2,
                    border: `1px solid ${theme.palette.grey[200]}`,
                    minHeight: 80,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                  }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.8rem', mb: 1, display: 'block' }}>
                      Daily Avg
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.1rem', color: themeColor.main }}>
                      {formatCurrencyNumber(genericReport.totalAmount / Object.keys(genericReport.valuePerDay).length)}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </ModernCard>
        </Grid>
      </Grid>

      {/* Category Breakdown Table - Enhanced spacing */}
      <ModernCard sx={{ mb: 6, boxShadow: theme.shadows[2] }}>
        <Box sx={{ 
          p: 3, 
          borderBottom: `1px solid ${theme.palette.divider}`,
          background: `linear-gradient(135deg, ${themeColor.light}10 0%, ${themeColor.main}05 100%)`,
        }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: themeColor.main, fontSize: '1.2rem', mb: 0.5 }}>
            Category Breakdown
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.9rem' }}>
            Detailed analysis by category with {isExpense ? 'budget' : 'target'} comparison
          </Typography>
        </Box>
        <Box sx={{ p: 3 }}>
          <MonthlyReportsCategoryTableComponent
            year={year}
            month={month}
            type={type}
            rows={monthlyReportsPerCategory}
            isCompact={true}
          />
        </Box>
      </ModernCard>

      {/* Daily Trend Chart - Enhanced spacing and final section */}
      <ModernCard sx={{ boxShadow: theme.shadows[2] }}>
        <Box sx={{ 
          p: 3, 
          borderBottom: `1px solid ${theme.palette.divider}`,
          background: `linear-gradient(135deg, ${themeColor.light}10 0%, ${themeColor.main}05 100%)`,
        }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: themeColor.main, fontSize: '1.2rem', mb: 0.5 }}>
            Daily Trend Analysis
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.9rem' }}>
            Day-by-day pattern and insights for {month} {year}
          </Typography>
        </Box>
        <Box sx={{ p: 4 }}>
          <MonthlyReportsPerDayChartComponent
            valuesPerDay={genericReport.valuePerDay}
            type={type}
          />
        </Box>
      </ModernCard>
    </Box>
  );
};

export default MonthlyReportsComponent;
